import { useState } from 'react';
import { Heart } from 'lucide-react';
import { api, type ProviderCard } from '../api';
import { haptic } from '../telegram';
import { useT } from '../i18n';
import { Avatar, Rating, Where, etb, fromPrice } from './ui';

/** Swipeable photos with dots, like an Instagram carousel. */
export function PhotoCarousel({ photos, ratio = 'aspect-[4/5]', onTap }: { photos: string[]; ratio?: string; onTap?: () => void }) {
  const [i, setI] = useState(0);
  if (!photos.length) return <div className={`${ratio} w-full bg-tg-secondary`} />;
  return (
    <div className={`relative ${ratio === 'h-full' ? 'h-full' : ''}`}>
      <div
        className={`no-scrollbar flex w-full snap-x snap-mandatory overflow-x-auto ${ratio}`}
        onScroll={(e) => {
          const el = e.currentTarget;
          setI(Math.round(el.scrollLeft / el.clientWidth));
        }}
        onClick={onTap}
      >
        {photos.map((src) => (
          <img key={src} src={src} alt="" className="h-full w-full shrink-0 snap-center object-cover" loading="lazy" draggable={false} />
        ))}
      </div>
      {photos.length > 1 && (
        <>
          <div className="absolute right-3 top-3 rounded-full bg-black/55 px-2 py-0.5 text-xs text-white">
            {i + 1}/{photos.length}
          </div>
          <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1.5">
            {photos.map((_, j) => (
              <span key={j} className={`h-1.5 w-1.5 rounded-full ${j === i ? 'bg-white' : 'bg-white/50'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Heart that saves a provider to favorites, updating instantly. */
export function FavoriteHeart({ p, onChange, size = 26 }: { p: ProviderCard; onChange?: (fav: boolean) => void; size?: number }) {
  const [fav, setFav] = useState(p.isFavorite);
  const toggle = async () => {
    haptic('select');
    const next = !fav;
    setFav(next);
    try {
      await (next ? api.addFavorite(p.id) : api.removeFavorite(p.id));
      onChange?.(next);
    } catch {
      setFav(!next);
    }
  };
  return (
    <button onClick={toggle} className="p-1 active:scale-90" aria-label="favorite">
      <Heart size={size} className={fav ? 'fill-red-500 text-red-500' : ''} />
    </button>
  );
}

/** A feed post: header, photos, actions, short bio. Tapping opens the full profile. */
export function ProviderPost({ p, onOpen, canBook }: { p: ProviderCard; onOpen: () => void; canBook: boolean }) {
  const t = useT();
  const price = fromPrice(p);
  return (
    <article className="mb-3 bg-tg-bg">
      <button className="flex w-full items-center gap-3 px-3 py-2.5 text-left" onClick={onOpen}>
        <Avatar src={p.photos[0]} name={p.displayName} size={38} ring={p.available} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-semibold">
            <span className="truncate">{p.displayName}</span>
            <span className="text-sm font-normal text-tg-hint">· {p.age}</span>
          </div>
          <Where p={p} />
        </div>
        <Rating avg={p.ratingAvg} count={p.ratingCount} />
      </button>
      <PhotoCarousel photos={p.photos} onTap={onOpen} />
      <div className="flex items-center gap-2 px-2 pt-1.5">
        {canBook && <FavoriteHeart p={p} />}
        <div className="flex-1 px-1 text-[15px]">
          {price !== null && <span className="font-semibold">{t('provider.from_price', { price: etb(price) })}</span>}
        </div>
        <button onClick={onOpen} className="rounded-xl bg-tg-button px-5 py-2 text-sm font-semibold text-tg-buttonText active:opacity-80">
          {canBook ? t('provider.book') : t('provider.about')}
        </button>
      </div>
      {p.bio && (
        <p className="line-clamp-2 px-3 pt-1 text-[15px]">
          <span className="font-semibold">{p.displayName}</span> {p.bio}
        </p>
      )}
    </article>
  );
}

/** Round avatars at the top of Home for providers who just switched on. */
export function StoriesRow({ items, onOpen }: { items: ProviderCard[]; onOpen: (p: ProviderCard) => void }) {
  const t = useT();
  if (!items.length) return null;
  return (
    <div className="border-b border-black/5 bg-tg-bg pb-2 pt-3">
      <div className="mb-2 px-4 text-sm font-semibold">{t('feed.available_now')}</div>
      <div className="no-scrollbar flex gap-4 overflow-x-auto px-4">
        {items.map((p) => (
          <button key={p.id} onClick={() => onOpen(p)} className="flex w-[68px] shrink-0 flex-col items-center gap-1">
            <Avatar src={p.photos[0]} name={p.displayName} size={64} ring />
            <span className="w-full truncate text-center text-xs">{p.displayName}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Compact grid tile, used for search results and other providers. */
export function ProviderTile({ p, onOpen }: { p: ProviderCard; onOpen: () => void }) {
  const t = useT();
  const price = fromPrice(p);
  return (
    <button onClick={onOpen} className="overflow-hidden rounded-2xl bg-tg-secondary text-left active:opacity-80">
      <div className="relative aspect-[3/4] w-full">
        {p.photos[0] ? <img src={p.photos[0]} alt="" className="h-full w-full object-cover" loading="lazy" /> : null}
        {p.available && <span className="absolute left-2 top-2 h-3 w-3 rounded-full border-2 border-white bg-green-500" />}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2.5 pb-2 pt-8 text-white">
          <div className="truncate font-semibold">
            {p.displayName}, {p.age}
          </div>
          {price !== null && <div className="text-xs opacity-90">{t('provider.from_price', { price: etb(price) })}</div>}
        </div>
      </div>
      <div className="flex items-center justify-between gap-1 px-2.5 py-2">
        <Where p={p} compact />
        <Rating avg={p.ratingAvg} count={p.ratingCount} />
      </div>
    </button>
  );
}
